/* bs-testimonios-home — carrusel de testimonios con dots mobile + nav arrows desktop */
/* Patrones tomados de bs-product-carousel pero adaptados:
   - Dots de paginacion en lugar de counter "1/N" (mobile)
   - Mismo flujo: scrollByCard con scrollTo explicito + checkOverflow + updateUi
   - Click en dot → scroll al card correspondiente */

(function () {
  'use strict';

  class BsTestimonials {
    constructor(section) {
      this.section = section;
      this.scroller = section.querySelector('[data-bs-testimonios-home-scroller]');
      this.row = this.scroller ? this.scroller.querySelector('.bs-testimonios-home__row') : null;
      // Dual nav: 1 set en header (visible tablet+), 1 set en pagination (visible mobile)
      this.prevBtns = Array.from(section.querySelectorAll('[data-direction="prev"]'));
      this.nextBtns = Array.from(section.querySelectorAll('[data-direction="next"]'));
      this.dotsContainer = section.querySelector('[data-bs-testimonios-home-dots]');
      this.dots = this.dotsContainer
        ? Array.from(this.dotsContainer.querySelectorAll('[data-bs-testimonios-home-dot]'))
        : [];
      this.cards = this.scroller ? this.scroller.querySelectorAll('.bs-testimonios-home__card') : [];
      this.totalCards = this.cards.length;

      if (!this.scroller || this.totalCards === 0) {
        return;
      }

      this.bindEvents();
      this.checkOverflow();
      this.updateUi();
    }

    bindEvents() {
      this.prevBtns.forEach((btn) => btn.addEventListener('click', () => this.scrollByCard(-1)));
      this.nextBtns.forEach((btn) => btn.addEventListener('click', () => this.scrollByCard(1)));

      // Click en cualquier dot → scrollTo del card correspondiente
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.bsTestimonialsDot, 10);
          if (Number.isFinite(idx)) this.scrollToIndex(idx);
        });
      });

      this.scroller.addEventListener('scroll', () => this.updateUi(), { passive: true });

      window.addEventListener(
        'resize',
        () => {
          this.checkOverflow();
          this.updateUi();
        },
        { passive: true }
      );
    }

    // Detecta si hay overflow real (mas cards que las que caben en el scroller).
    // Mobile/tablet: siempre overflow (1-2 cards visibles vs >=3 testimonials).
    // Desktop: solo overflow si hay >5 blocks (el .pen muestra 5 cards space-between).
    // El atributo se aplica al scroller (no al section) para que matchee el selector CSS
    // .bs-testimonios-home__scroller[data-overflow='true'].
    checkOverflow() {
      if (!this.scroller) return;
      const tolerance = 1;
      const isOverflowing = this.scroller.scrollWidth > this.scroller.clientWidth + tolerance;
      if (isOverflowing) {
        this.scroller.dataset.overflow = 'true';
      } else {
        this.scroller.removeAttribute('data-overflow');
      }
    }

    getStep() {
      // Step = card width + gap. Lee del DOM por si cambia con resize.
      if (!this.cards[0]) return 0;
      const cardWidth = this.cards[0].offsetWidth;
      let gap = 16;
      if (this.row) {
        const computed = window.getComputedStyle(this.row);
        const parsedGap = parseFloat(computed.gap || computed.columnGap);
        if (!Number.isNaN(parsedGap)) gap = parsedGap;
      }
      return cardWidth + gap;
    }

    scrollByCard(direction) {
      const step = this.getStep();
      if (step <= 0) return;

      const prefersReduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = prefersReduced ? 'auto' : 'smooth';

      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      const currentScrollLeft = this.scroller.scrollLeft;

      // Cards evenly spaced (step = card-width + gap uniforme) → currentScrollLeft - step
      // siempre cae en el snap-start anterior, incluso desde snap-end del ultimo card.
      let target = direction > 0 ? currentScrollLeft + step : currentScrollLeft - step;

      target = Math.max(0, Math.min(target, maxScroll));
      this.scroller.scrollTo({ left: target, behavior });
    }

    scrollToIndex(idx) {
      const step = this.getStep();
      if (step <= 0) return;
      const prefersReduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = prefersReduced ? 'auto' : 'smooth';

      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      const target = Math.max(0, Math.min(idx * step, maxScroll));
      this.scroller.scrollTo({ left: target, behavior });
    }

    /**
     * Calcula el indice del card "activo" basado en scrollLeft. Retorna entero 0..totalCards-1.
     * Logica: el card activo es el que tiene su edge izquierdo mas cercano al scrollLeft actual.
     */
    getActiveIndex() {
      const step = this.getStep();
      if (step <= 0) return 0;
      const scrollLeft = this.scroller.scrollLeft;
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;

      // Edge case: end-aligned ultimo card → activeIdx = totalCards - 1
      if (scrollLeft >= maxScroll - 2 && maxScroll > 0) {
        return this.totalCards - 1;
      }
      const idx = Math.round(scrollLeft / step);
      return Math.max(0, Math.min(idx, this.totalCards - 1));
    }

    updateUi() {
      const scrollLeft = this.scroller.scrollLeft;
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;

      const prevDisabled = scrollLeft < 1;
      this.prevBtns.forEach((btn) => {
        btn.disabled = prevDisabled;
        btn.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
      });
      const nextDisabled = scrollLeft >= maxScroll - 1;
      this.nextBtns.forEach((btn) => {
        btn.disabled = nextDisabled;
        btn.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
      });

      // Actualizar dot activo.
      // Pablo: aria-current (NO aria-selected). aria-selected pertenece a role tab/option/etc;
      // los dots ahora son role="group" con buttons → aria-current="true" semantica correcta.
      if (this.dots.length > 0) {
        const activeIdx = this.getActiveIndex();
        this.dots.forEach((dot, i) => {
          const isActive = i === activeIdx;
          dot.classList.toggle('bs-testimonios-home__dot--active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }
  }

  function init(root) {
    const sections = (root || document).querySelectorAll('.bs-testimonios-home');
    sections.forEach((section) => {
      if (section.__bsTestimonialsInit) return;
      section.__bsTestimonialsInit = true;
      new BsTestimonials(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Re-init cuando el customizer recarga la section
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.bs-testimonios-home');
    if (section) {
      section.__bsTestimonialsInit = false;
      init(event.target);
    }
  });
})();
